import { ChainsIcon } from 'modules/shared-components/exchange/exchange-icon';
import Tooltip from 'modules/shared-components/tooltip';
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import { BotType, DexSniperBot } from 'api/types/httpsTypes/dex-bot';
import { twMerge } from 'tailwind-merge';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import moment from 'moment';
import { useState } from 'react';

import {
  ChartBarSquareIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  ExclamationCircleIcon,
  PauseCircleIcon,
  PencilIcon,
  PhoneIcon,
  PlayCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { ResumeDexBotModal } from '../../components/Bots/ResumeBotModal';
import { PauseDexBotModal } from '../../components/Bots/PauseBotModal';
import { DeleteDexBotModal } from '../../components/Bots/DeleteBotModal';
import { chainAsset } from 'api/types/httpsTypes/d-wallets';
import { formatNumberWithSuffix } from 'utils/FormatNumber';
import Big from 'big.js';
import { BaseButton } from 'modules/shared-components/button/base-button';
import { formatUUID } from 'utils/uuid';
import { CopyText } from 'modules/shared-components/copy';
import { MaxFundsNeeded } from '../volatility/sections/VolatilitySettings';
import {
  ResponsiveMenu,
  ResponsiveMenuItem,
} from '../../components/dropdown/menu/ResponsiveMenu';
import { Tabs } from '../../components/layout/SIDEBAR_CONST';
import { BotsSortKeys } from './usePageParams';
import { fromAmount } from '../../components/chains/units';
import { PerformanceStyle } from '../../components/alerts/performanceStyle';

type Action = 'edit' | 'pause' | 'resume' | 'delete';

export function BotActions({
  bot,
  className,
  botType,
}: {
  bot: DexSniperBot;
  className?: string;
  botType: BotType;
}) {
  const [activeAction, setActiveAction] = useState<Action>();
  const handleClose = () => setActiveAction(undefined);

  return (
    <div
      className="xxs:flex xxs:gap-2 justify-end z-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <ResponsiveMenu
        dropdownPlacement="bottom-end"
        color="dexNeutral"
        className={twMerge('xxs:p-1', className)}
        label="Bot actions"
      >
        {bot.status === 'ACTIVE' && (
          <ResponsiveMenuItem onClick={() => setActiveAction('pause')}>
            <PauseCircleIcon className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4" />{' '}
            <span className="xxs:text-base lg:text-xs">Pause</span>
          </ResponsiveMenuItem>
        )}
        {bot.status === 'PAUSED' && (
          <ResponsiveMenuItem onClick={() => setActiveAction('resume')}>
            <PlayCircleIcon className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4" />{' '}
            <span className="xxs:text-base lg:text-xs">Resume</span>
          </ResponsiveMenuItem>
        )}
        <ResponsiveMenuItem onClick={() => setActiveAction('delete')}>
          <TrashIcon className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4" />{' '}
          <span className="xxs:text-base lg:text-xs">Delete</span>
        </ResponsiveMenuItem>
        <ResponsiveMenuItem
          href={`/dex/bots/${
            botType === BotType.Volatility
              ? Tabs.VolatilityBot
              : botType === BotType.Wallet
              ? Tabs.WalletCopyTrading
              : Tabs.TgCopyTrading
          }/${bot.id}/edit`}
        >
          <PencilIcon className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4" />{' '}
          <span className="xxs:text-base lg:text-xs">Edit</span>
        </ResponsiveMenuItem>
        <ResponsiveMenuItem
          href={`/dex/bots/${
            botType === BotType.Volatility
              ? Tabs.VolatilityBot
              : botType === BotType.Wallet
              ? Tabs.WalletCopyTrading
              : Tabs.TgCopyTrading
          }/${bot.id}/duplicate`}
        >
          <DocumentDuplicateIcon className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4" />{' '}
          <span className="xxs:text-base lg:text-xs">Duplicate</span>
        </ResponsiveMenuItem>
      </ResponsiveMenu>
      {activeAction === 'resume' && (
        <ResumeDexBotModal
          id={bot.id}
          handleClose={handleClose}
          botType={botType}
        />
      )}
      {activeAction === 'pause' && (
        <PauseDexBotModal
          id={bot.id}
          handleClose={handleClose}
          botType={botType}
        />
      )}
      {activeAction === 'delete' && (
        <DeleteDexBotModal
          bot={bot}
          handleClose={handleClose}
          botType={botType}
        />
      )}
    </div>
  );
}

export const desktopColumns = ({
  botType,
}: {
  botType: BotType;
}): TableColumn<DexSniperBot>[] => {
  return [
    {
      label: (
        <Tooltip text="The unique identifier of the bot.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Id
          </div>
        </Tooltip>
      ),
      key: `ID`,
      component: ({ data }) => (
        <div className="flex space-x-1 items-center">
          {data.insufficientBalance && (
            <Tooltip text="Your wallet balance is running low! Please top up soon to prevent your bot from being automatically paused.">
              <div className="flex justify-center items-center">
                <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-5 xxs:h-5" />
              </div>
            </Tooltip>
          )}
          <CopyText textToCopy={data.id}>
            <BaseButton
              type="button"
              className="text-black-500 hover:text-black-700 dark:text-black-200 dark:hover:text-black-100"
            >
              <div className="xxs:text-xs">{formatUUID(data.id)}</div>
            </BaseButton>
          </CopyText>
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The bot name and chain.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Name
          </div>
        </Tooltip>
      ),
      key: `BOT`,
      sortableKey: BotsSortKeys.Name,
      component: ({ data }) => (
        <div className="flex items-center xxs:gap-x-1">
          <div className="flex">
            <div
              className={twMerge(
                'text-dex-white xxs:text-xs rounded xxs:p-0.5 ',
                data.status === 'ACTIVE'
                  ? 'bg-green-600 text-green-500'
                  : data.status === 'PAUSED'
                  ? 'bg-yellow-600 text-yellow-500'
                  : 'bg-red-600 text-red-500'
              )}
            >
              <div className="flex space-x-1 items-center">
                {data.status === 'ACTIVE' ? (
                  <PlayCircleIcon className="w-4 h-4" />
                ) : (
                  <PauseCircleIcon className="w-4 h-4" />
                )}
              </div>
            </div>
          </div>
          <div className="relative">
            <ChainsIcon imgName={data.chain} />
          </div>
          <Tooltip text={data.name}>
            <div className="max-w-[130px] xxs:text-xs overflow-hidden ">
              <div className="truncate">{data.name}</div>
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The total gross realized PnL of the bot during the last 30 days.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            30D PnL
          </div>
        </Tooltip>
      ),
      key: `PNL_${botType}`,
      sortableKey: BotsSortKeys.PnlDollar,
      component: ({ data }) =>
        data.analytics?.pnlDollar ? (
          <div>
            <PerformanceStyle
              pnl={data.analytics.pnlDollar}
              className="xxs:text-xs"
            />
          </div>
        ) : (
          <div className="xxs:text-xs ">--</div>
        ),
    },
    {
      label: (
        <Tooltip text="The total volume traded by the bot during the last 30 days.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            30D Volume
          </div>
        </Tooltip>
      ),
      sortableKey: BotsSortKeys.VolumeDollar,
      key: `VOLUME_${botType}`,
      component: ({ data }) =>
        data.analytics?.volumeDollar ? (
          <div className="xxs:text-xs ">
            ${formatNumberWithSuffix(data.analytics.volumeDollar)}
          </div>
        ) : (
          <div className="xxs:text-xs ">--</div>
        ),
    },
    {
      label: (
        <Tooltip text="The total gas fees paid during the last 30 days.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Gas Fees
          </div>
        </Tooltip>
      ),
      key: `GAS_${botType}`,
      component: ({ data }) =>
        data.analytics?.gasFeeDollar ? (
          <div className="flex space-x-1 items-center xxs:text-xs">
            <div>${formatNumberWithSuffix(data.analytics.gasFeeDollar)}</div>
            {Big(data.analytics.gasFeeVolumeRatio).gt(0.1) && (
              <Tooltip text="Your gas fees are too high compared to your trading volume. To improve efficiency, consider increasing your order size.">
                <ExclamationCircleIcon className="w-5 h-5 xxs:text-red-500 hover:text-red-600" />
              </Tooltip>
            )}
          </div>
        ) : (
          <div className="xxs:text-xs ">--</div>
        ),
    },
    {
      label: (
        <Tooltip text="The current active positions vs the maximum n. of positions allowed.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Positions
          </div>
        </Tooltip>
      ),
      key: `POSITIONS_${botType}`,
      component: ({ data }) => (
        <div className="xxs:text-xs ">
          {data.numActivePositions}/
          {botType === BotType.Volatility
            ? data.addresses?.length ?? 0
            : data.maxNumActivePositions}
        </div>
      ),
    },

    {
      label: (
        <Tooltip text="The maximum capital allowed by the bot.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Max. Capital
          </div>
        </Tooltip>
      ),
      key: `MAX_INVESTMENT_${botType}`,
      component: ({ data }) => (
        <div className="xxs:text-xs ">
          {botType === BotType.Volatility
            ? formatNumberWithSuffix(
                MaxFundsNeeded({
                  am: parseFloat(data.position.buyAmountInWei ?? 0) ?? 0,
                  dcaO:
                    data.position.limitOrders?.map((order) => {
                      return {
                        amount: parseFloat(order.amount ?? 0),
                        slippage: order.slippage ?? 0,
                        priceDeviationPercentage: parseFloat(
                          order.priceDeviationPercentage ?? 0
                        ),
                      };
                    }) ?? undefined,
                  ad: data.addresses?.map((a) => {
                    return {
                      address: a,
                    };
                  }),
                  isDca: data.position.limitOrders?.length > 0,
                  isFromWei: true,
                  chain: data.chain,
                })
              )
            : formatNumberWithSuffix(
                Big(data.maxNumActivePositions ?? 0)
                  .mul(
                    fromAmount(
                      data.position.buyAmountInWei ?? 0,
                      data.chain,
                      'weiToEther'
                    ) ?? 0
                  )
                  .mul(
                    data.copyBuyMore
                      ? data.maxBuyMore
                        ? data.maxBuyMore + 1
                        : 1
                      : 1
                  ),
                { precision: 2 }
              )}{' '}
          {chainAsset(data.chain)}
        </div>
      ),
    },

    {
      label: (
        <Tooltip
          text={
            botType === BotType.Volatility
              ? 'The number of tokens that the bot is allowed to trade'
              : `The ${
                  botType === BotType.Wallet ? 'wallet' : 'channel(s)'
                } that is copy-traded.`
          }
        >
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            {botType === BotType.Wallet
              ? 'Wallet'
              : botType === BotType.Volatility
              ? 'Tokens'
              : 'Channels'}
          </div>
        </Tooltip>
      ),
      key:
        botType === BotType.Wallet
          ? 'WALLET'
          : botType === BotType.Volatility
          ? 'TOKENS'
          : 'CHANNELS',
      component: ({ data }) =>
        botType === BotType.Wallet ? (
          data.walletAddressToCopy ? (
            <EthAddress
              className="xxs:text-xs"
              address={data.walletAddressToCopy}
              chain={data.chain}
              kind="account"
              precision={3}
            />
          ) : (
            <div className="xxs:text-xs ">--</div>
          )
        ) : botType === BotType.Volatility ? (
          <div className="xxs:text-xs ">{data.addresses?.length ?? 0}</div>
        ) : (
          <div className="xxs:text-xs ">
            {data.telegramChannelFilter?.subscribedChannelsIds?.length ?? 0}
          </div>
        ),
    },

    {
      label: (
        <Tooltip text="The date in which the bot has been created.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Creation Date
          </div>
        </Tooltip>
      ),
      sortableKey: BotsSortKeys.CreatedAt,
      key: `CREATION_DATE_${botType}`,
      component: ({ data }) => (
        <div className="flex space-x-1 items-center xxs:text-xs">
          {moment(data.createdAt).format('DD/MM/YY HH:mm')}
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="Actions that can be performed on this bot.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Actions
          </div>
        </Tooltip>
      ),

      key: `ACTIONS_${botType}`,
      component: ({ data }) => (
        <div className="flex space-x-1 items-center justify-end">
          <BotActions bot={data} botType={botType} />
        </div>
      ),
    },
  ];
};

export const mobileColumns = ({
  botType,
}: {
  botType: BotType;
}): TableColumn<DexSniperBot>[] => {
  return [
    {
      key: 'WALLETS',
      component: ({ data: bot }) => (
        <div className=" xxs:p-2 w-full xxs:text-xs flex justify-between xxs:gap-2 items-center xxs:text-dex-white-secondary">
          <div className="flex flex-col">
            <div className="flex items-center xxs:gap-x-1">
              {bot.insufficientBalance && (
                <Tooltip text="Your wallet balance is running low! Please top up soon to prevent your bot from being automatically paused.">
                  <div className="flex justify-center items-center">
                    <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-5 xxs:h-5" />
                  </div>
                </Tooltip>
              )}
              <div className="flex">
                <div
                  className={twMerge(
                    'text-dex-white xxs:text-xs rounded xxs:p-0.5 ',
                    bot.status === 'ACTIVE'
                      ? 'bg-green-600 text-green-500'
                      : bot.status === 'PAUSED'
                      ? 'bg-yellow-600 text-yellow-500'
                      : 'bg-red-600 text-red-500'
                  )}
                >
                  <div className="flex space-x-1 items-center">
                    {bot.status === 'ACTIVE' ? (
                      <PlayCircleIcon className="w-3 h-3 " />
                    ) : (
                      <PauseCircleIcon className="w-3 h-3 " />
                    )}
                  </div>
                </div>
              </div>

              <div className="relative">
                <ChainsIcon imgName={bot.chain} />
              </div>
              <Tooltip text={bot.name}>
                <div className="max-w-[220px] overflow-hidden truncate  xxs:text-dex-white">
                  <div>{bot.name}</div>
                </div>
              </Tooltip>
            </div>

            <div className="flex space-x-1 items-center">
              <div className="text-xs">
                Max:{' '}
                {botType == BotType.Volatility
                  ? formatNumberWithSuffix(
                      MaxFundsNeeded({
                        am: parseFloat(bot.position.buyAmountInWei ?? 0) ?? 0,
                        dcaO:
                          bot.position.limitOrders?.map((order) => {
                            return {
                              amount: parseFloat(order.amount ?? 0),
                              slippage: order.slippage ?? 0,
                              priceDeviationPercentage: parseFloat(
                                order.priceDeviationPercentage ?? 0
                              ),
                            };
                          }) ?? undefined,
                        ad: bot.addresses?.map((a) => {
                          return {
                            address: a,
                          };
                        }),
                        isDca: bot.position.limitOrders?.length > 0,
                        isFromWei: true,
                        chain: bot.chain,
                      })
                    )
                  : formatNumberWithSuffix(
                      Big(bot.maxNumActivePositions ?? 0)
                        .mul(
                          fromAmount(
                            bot.position.buyAmountInWei ?? 0,
                            bot.chain,
                            'weiToEther'
                          ) ?? 0
                        )
                        .mul(bot.copyBuyMore ? bot.maxBuyMore ?? 1 : 1),
                      { precision: 1 }
                    )}{' '}
                {chainAsset(bot.chain)}
              </div>
              <div>
                {botType === BotType.Wallet ? (
                  bot.walletAddressToCopy ? (
                    <div className="flex space-x-0.5 items-center">
                      <CreditCardIcon className="h-4 w-4" />
                      <EthAddress
                        address={bot.walletAddressToCopy}
                        chain={bot.chain}
                        kind="account"
                        precision={2}
                      />
                    </div>
                  ) : (
                    <div>--</div>
                  )
                ) : botType === BotType.Volatility ? (
                  <div className="flex space-x-0.5 items-center">
                    <ChartBarSquareIcon className="h-4 w-4" />
                    <div className="">{bot.addresses?.length ?? 0}</div>
                  </div>
                ) : (
                  <div className="flex space-x-0.5 items-center">
                    <PhoneIcon className="h-4 w-4" />
                    <div className="">
                      {bot.telegramChannelFilter?.subscribedChannelsIds
                        ?.length ?? 0}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2 items-center ">
            <div className="flex items-end flex-col xxs:text-dex-white">
              {bot.analytics?.pnlDollar ? (
                <div>
                  <PerformanceStyle
                    className="xxs:text-base"
                    pnl={bot.analytics.pnlDollar}
                  />
                </div>
              ) : (
                <div className="text-base">--</div>
              )}

              <div className="xxs:text-xs xxs:font-normal text-center">
                {bot.numActivePositions}/
                {botType === BotType.Volatility
                  ? bot.addresses?.length ?? 0
                  : bot.maxNumActivePositions}
              </div>
            </div>
            <div className="flex justify-end grow-0">
              <BotActions bot={bot} botType={botType} />
            </div>
          </div>
        </div>
      ),
    },
  ];
};
